/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { Summary } from '../../../types/api';
import { formatEvalLabel } from '../../../lib/utils';
import { ENUM_INDEX_COLORS, FALLBACK_COLORS } from '../../../utils/constants';

type ArcSegment = {
  key: string;
  label: string;
  value: number;
  color: string;
};

type CircularArcGraphProps = {
  enumCounts?: Summary['enum_counts'];
  templateEnums?: string[];
};

const CircularArcGraph = ({ enumCounts, templateEnums }: CircularArcGraphProps) => {
  const counts = enumCounts ?? {};

  const entries = Object.entries(counts).filter(([, value]) =>
    Number.isFinite(value)
  );

  const sortedEntries = [...entries].sort(([firstKey], [secondKey]) => {
    const firstOrder = templateEnums?.includes(firstKey)
      ? templateEnums.indexOf(firstKey)
      : Number.MAX_SAFE_INTEGER;
    const secondOrder = templateEnums?.includes(secondKey)
      ? templateEnums.indexOf(secondKey)
      : Number.MAX_SAFE_INTEGER;

    if (firstOrder !== secondOrder) return firstOrder - secondOrder;
    return firstKey.localeCompare(secondKey);
  });

  const arcSegments: ArcSegment[] = sortedEntries.map(([key, value], index) => {
    const enumIndex = templateEnums?.includes(key)
      ? templateEnums.indexOf(key)
      : undefined;

    const color = enumIndex !== undefined
      ? ENUM_INDEX_COLORS[enumIndex] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length]
      : FALLBACK_COLORS[index % FALLBACK_COLORS.length];

    return {
      key,
      label: formatEvalLabel(key),
      value,
      color,
    };
  });

  const arcTotal = arcSegments.reduce((sum, segment) => sum + segment.value, 0);
  const getPercentage = (value: number) => {
    if (value === 0) return '0%';
    if (arcTotal <= 0) return '0%';

    const percentage = (value / arcTotal) * 100;
    return Number.isInteger(percentage) ? `${percentage}%` : `${percentage.toFixed(2)}%`;
  };
  const radius = 27;
  const circumference = 2 * Math.PI * radius;
  const baseStroke = '#0A343C';
  let strokeOffset = 0;

  return (
    <div className="flex items-center gap-3">
      <div className="relative w-[72px] h-[72px] shrink-0">
        <svg
          width="67"
          height="67"
          viewBox="0 0 67 67"
          className="block -rotate-90"
          aria-label="Evaluation summary graph"
        >
          <circle
            cx="35"
            cy="35"
            r={radius}
            fill="none"
            stroke={baseStroke}
            strokeWidth="5"
          />
          {arcTotal > 0
            ? arcSegments.map((segment) => {
                const arcLength = (segment.value / arcTotal) * circumference;
                const strokeDasharray = `${arcLength} ${circumference - arcLength}`;
                const currentOffset = strokeOffset;
                strokeOffset += arcLength;

                return (
                  <circle
                    key={segment.key}
                    cx="35"
                    cy="35"
                    r={radius}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth="5"
                    strokeLinecap="butt"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={-currentOffset}
                  />
                );
              })
            : null}
        </svg>
      </div>
      <ul className="space-y-1.5">
        {arcSegments.map((segment) => (
          <li key={segment.key} className="flex items-center gap-2 text-xs text-[#C1C9CF]">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: segment.color }}
            ></span>
            <span className="text-[#8C9EA1] capitalize">{segment.label}</span>
            <span className="text-white">{getPercentage(segment.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CircularArcGraph;
